import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { io, Socket } from 'socket.io-client';
import { MessageService } from 'primeng/api';
import { IChatResponseMessage } from '@common/models/Chat/IChatResponseMessage';
import { SummaryGeneratedNotificationPayload, NotificationData } from "@common/types/Notifications";


@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  webSocketConnection: boolean = false;
  private socket: Socket;
  private socketHost: string;

  constructor(
    private readonly messageService: MessageService
  ) {
    // Determine WebSocket host based on environment
    this.socketHost = environment.baseURL.replace(/^https/, 'wss').replace(/\/api\/v1\/?$/, '');
    if (environment.envName === 'local') {
      this.socketHost = 'ws://localhost:8080';
    }

  }

  openSocket(userId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.socket) {
        console.debug('Socket is already open.');
        return resolve(); // Resolve immediately if socket is already open
      }

      // Connect socket
      this.socket = io(this.socketHost, {
        transports: ['websocket'],
        reconnection: true,            // Enable reconnection
        reconnectionAttempts: 20,       // Number of reconnection attempts
        reconnectionDelay: 1000,       // Delay between reconnection attempts (in ms)
        auth: {
          userId: userId
        }
      });

      // Handle reconnection attempt
      this.socket.on('reconnect_attempt', (attemptNumber) => {
        console.debug(`Reconnection attempt #${attemptNumber}`);
      });

      // Reconnected after being disconnected
      this.socket.on('reconnect', (attemptNumber) => {
        console.debug(`Reconnected after attempt #${attemptNumber}`);
        this.webSocketConnection = true;
        resolve();
      });

      // Handle reconnection error
      this.socket.on('reconnect_error', (error) => {
        console.error('Reconnection failed with error:', error);
      });

      // Max reconnection attempts reached
      this.socket.on('reconnect_failed', () => {
        console.error('Failed to reconnect after maximum attempts.');
        this.webSocketConnection = false;
        reject(new Error('Failed to reconnect after maximum attempts'));
      });

      // Handle successful connection
      this.socket.on('connect', () => {
        this.webSocketConnection = true;
        console.debug('Socket connected');
        resolve();
      });

      // Handle disconnection
      this.socket.on('disconnect', () => {
        this.webSocketConnection = false;
        console.debug('Socket disconnected');
      });

      this.socket.on('connect_error', (err) => {
        console.error('Connection failed:', err);
        reject(new Error('Connection failed'));
      });

      this.socket.on('error', (error: any) => {
        console.error('Socket error', error);
        reject(error); // Reject on error
      });

      this.socket.on('notification', (data: NotificationData<unknown>) => {
        this.notifyUser(data);
      });
    });
  }

  notifyUser(data: NotificationData<unknown>) {
    const { type } = data;
    if (type === 'documentSummaryGenerated') {
      const { documentName } = data.payload as SummaryGeneratedNotificationPayload;;
      this.messageService.add({
        severity: 'info',
        summary: 'Document Summary Generated',
        detail: `Document summary generated for document: ${documentName}`,
        life: 5000
      });
    } else if (type === 'generalNotification') {
      this.messageService.add({
        severity: 'info',
        summary: 'Notification',
        detail: `You have a new notification.`,
        life: 5000
      });
    } else {
      console.warn('Unknown notification type received:', type);
    }
  }

  joinRoom(roomId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.socket?.connected) {
        this.socket.emit('joinRoom', roomId);
        console.debug(`Joining room: ${roomId}`);

        const timeout = setTimeout(() => {
          this.socket.off('socketConnected', handleSocketConnected);
          reject(new Error('No response from server for joinRoom'));
        }, 5000); // Wait max 5s for a response

        const handleSocketConnected = () => {
          clearTimeout(timeout);
          this.socket.off('socketConnected', handleSocketConnected);
          console.debug(`Joined room: ${roomId} confirmed by server`);
          resolve();
        };

        this.socket.once('socketConnected', handleSocketConnected);

      } else {
        const errorMsg = 'Cannot join room. Socket is not connected.';
        console.error(errorMsg);
        reject(new Error(errorMsg));
      }
    });
  }


  async connectAndJoinRoom(userId: string, roomId: string): Promise<boolean> {
    try {
      await this.connectToSocket(userId);
      await this.joinRoom(roomId);
      console.debug('Room joined successfully!');
      return true;
    } catch (error) {
      console.error('Error in connectAndJoinRoom:', error);
      return false;
    }
  }



  async connectToSocket(userId: string): Promise<void> {
    try {
      if (!this.socket || !this.socket.connected) {
        await this.openSocket(userId);
        await this.waitForConnection();
      }
      console.debug('WebSocket connection established!');
    } catch (error) {
      console.error('Error establishing WebSocket connection:', error);
      this.socket = undefined!;
      this.webSocketConnection = false;
    }
  }


  getMessages(): Observable<IChatResponseMessage> {
    return new Observable<IChatResponseMessage>(observer => {
      this.socket.on('message', (data: IChatResponseMessage) => {
        observer.next(data);
      });

      this.socket.on('error', (error: any) => {
        console.error('Socket error', error);
        observer.error(error);
      });

      this.socket.on('close', () => {
        console.debug('Socket Connection closed');
        observer.complete();
      });
    });
  }

  async isWebSocketConnected(): Promise<boolean> {
    if (!this.socket?.connected) {
      console.debug('WebSocket is currently disconnected.');
      return false;
    }

    try {
      const verified = await this.verifyConnection();
      console.debug('WebSocket ping-pong verified: connected');
      return verified;
    } catch (err) {
      console.error('WebSocket ping-pong failed:', err);
      return false;
    }
  }


  verifyConnection(timeout: number = 5000): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      if (!this.socket?.connected) {
        return reject(new Error('Socket is not connected'));
      }

      const timeoutId = setTimeout(() => {
        this.socket.off('pong', handlePong);
        reject(new Error('Pong not received in time'));
      }, timeout);

      const handlePong = () => {
        clearTimeout(timeoutId);
        this.socket.off('pong', handlePong);
        resolve(true);
      };

      this.socket.once('pong', handlePong);
      this.socket.emit('ping');
    });
  }



  closeConnection(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = undefined!;
    }
  }

  waitForConnection(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (this.socket?.connected) {
        resolve();
      } else {
        this.socket?.once('connect', () => resolve());
        this.socket?.once('connect_error', (err) => reject(`Connection failed: ${err}`));
      }
    });
  }

}
